import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Archive, ShieldAlert, Loader2, FlaskConical } from "lucide-react";

const GROUPS = [
  { key: "suppliers", label: "Suppliers", endpoint: "/suppliers", nameKey: "name", idField: "id", codeKey: "code" },
  { key: "buyers", label: "Buyers", endpoint: "/buyers", nameKey: "name", idField: "id", codeKey: "buyerId" },
  { key: "products", label: "Products", endpoint: "/products", nameKey: "name", idField: "id", codeKey: "productId" },
  { key: "recipes", label: "Recipes", endpoint: "/recipes", nameKey: "productName", idField: "id", codeKey: "recipeNumber" },
];

const isTest = (name) => String(name || "").toUpperCase().startsWith("ZZ WEB VERIFY");

export default function CleanupPanel() {
  const qc = useQueryClient();
  const rSup = useResource("/suppliers");
  const rBuy = useResource("/buyers");
  const rProd = useResource("/products");
  const rRec = useResource("/recipes");
  const results = [{ g: GROUPS[0], q: rSup }, { g: GROUPS[1], q: rBuy }, { g: GROUPS[2], q: rProd }, { g: GROUPS[3], q: rRec }];
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const anyLoading = results.some((r) => r.q.isLoading);

  const doAction = async (archive = false) => {
    const { g, row } = target;
    setBusy(true);
    try {
      if (archive) {
        await api.put(`${g.endpoint}/${row[g.idField]}`, { active: false });
        toast.success(`${g.label.slice(0, -1)} archived`);
      } else {
        await api.delete(`${g.endpoint}/${row[g.idField]}`);
        toast.success(`${g.label.slice(0, -1)} deleted`);
      }
      qc.invalidateQueries({ queryKey: ["resource", g.endpoint] });
      setTarget(null);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) toast.error("Delete blocked (referenced elsewhere). Use Archive instead.");
      else toast.error(apiErrorMessage(e, "Action failed"));
    } finally { setBusy(false); }
  };

  if (anyLoading) return <div className="space-y-6"><PageHeader title="Verification Records" /><LoadingState /></div>;

  const totalTest = results.reduce((s, r) => s + (Array.isArray(r.q.data) ? r.q.data.filter((x) => isTest(x[r.g.nameKey])).length : 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Verification Records" subtitle="Test records created during web integration (prefixed “ZZ Web Verify”). Review and remove as you wish." />

      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm text-amber-900">
        <FlaskConical className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Only records named “ZZ Web Verify …” are shown here. Real business data (Percept Foods, Join Commerce, their products/orders) is intentionally NOT listed. Delete removes the record; if the backend blocks it (referenced elsewhere), use Archive.</span>
      </div>

      {totalTest === 0 && (
        <div className="rounded-lg border border-dashed border-stone-300 bg-white/60 py-12 text-center text-sm text-muted-foreground">
          No “ZZ Web Verify” test records remain. 🎉
        </div>
      )}

      {GROUPS.map((g, i) => {
        const rows = (Array.isArray(results[i].q.data) ? results[i].q.data : []).filter((x) => isTest(x[g.nameKey]));
        if (rows.length === 0) return null;
        return (
          <div key={g.key} className="rounded-xl border border-stone-200 bg-white shadow-sm">
            <div className="border-b border-stone-200 bg-stone-50 px-4 py-2.5 font-display text-sm font-semibold text-stone-800">{g.label}</div>
            <div className="divide-y divide-stone-100">
              {rows.map((row) => (
                <div key={row[g.idField]} data-testid={`cleanup-${g.key}-${row[g.idField]}`} className="flex items-center justify-between px-4 py-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-stone-800">{row[g.nameKey]}</div>
                    <div className="font-mono text-[11px] text-stone-400">{row[g.codeKey] || row[g.idField]}{row.active === false ? " · archived" : ""}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`cleanup-archive-${row[g.idField]}`} size="sm" variant="outline" className="h-8 gap-1.5" disabled={row.active === false} onClick={() => setTarget({ g, row, mode: "archive" })}>
                      <Archive className="h-3.5 w-3.5" /> Archive
                    </Button>
                    <Button data-testid={`cleanup-delete-${row[g.idField]}`} size="sm" variant="outline" className="h-8 gap-1.5 text-rose-600" onClick={() => setTarget({ g, row, mode: "delete" })}>
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-rose-600" /> {target?.mode === "archive" ? "Archive" : "Delete"} test record</AlertDialogTitle>
            <AlertDialogDescription>
              {target?.mode === "archive" ? "Mark" : "Permanently remove"} <b>{target?.row?.[target?.g?.nameKey]}</b> {target?.mode === "archive" ? "as inactive" : ""} in the live ERP?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cleanup-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="cleanup-confirm" className={target?.mode === "archive" ? "bg-amber-700 hover:bg-amber-800" : "bg-rose-600 hover:bg-rose-700"} onClick={() => doAction(target?.mode === "archive")} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : target?.mode === "archive" ? "Archive" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
